// 📁 src/components/Topbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiSettings, FiBell, FiLogOut, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import ProfileMenu from "./ProfileMenu";
import noProfile from "../assets/No_Profile.jpg";
import "./ProfileMenu.css";
import "./Topbar.css";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

function Topbar({ onLogout, user }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [userData, setUserData] = useState(user);
  const [greeting, setGreeting] = useState("");
  const [emoji, setEmoji] = useState("😊");

  // 🔔 Estado de notificaciones
  const [notificaciones, setNotificaciones] = useState([]);
  const [showNotificaciones, setShowNotificaciones] = useState(false);
  const notifRef = useRef(null);
  const navigate = useNavigate();

  // ID universal
  const userId = userData?._id || userData?.id;

  // Cargar usuario
  useEffect(() => {
    const stored = user || JSON.parse(localStorage.getItem("user"));
    if (stored) setUserData(stored);
  }, [user]);

  // Saludo dinámico
  useEffect(() => {
    const actualizarSaludo = () => {
      const h = new Date().getHours();
      if (h >= 5 && h < 12) {
        setGreeting("¡Buenos días");
        setEmoji("☀️");
      } else if (h >= 12 && h < 18) {
        setGreeting("¡Buenas tardes");
        setEmoji("🌤️");
      } else {
        setGreeting("¡Buenas noches");
        setEmoji("🌛");
      }
    };

    actualizarSaludo();
    const interval = setInterval(actualizarSaludo, 600000);
    return () => clearInterval(interval);
  }, []);

  // Cargar notificaciones
  useEffect(() => {
    if (!userId) return;

    const fetchNotificaciones = async () => {
      try {
        const res = await fetch(`${API_URL}/api/notificaciones/${userId}`);
        const data = await res.json();
        setNotificaciones(data || []);
      } catch (err) {
        console.error("❌ Error cargando notificaciones:", err);
      }
    };

    fetchNotificaciones(); // llamada inicial (opcional)

      const interval = setInterval(fetchNotificaciones, 2 * 60 * 60 * 1000); // 2 horas

      return () => clearInterval(interval);
    }, [userId]);

  // Marcar como leída
  const marcarComoLeida = async (id) => {
    try {
      await fetch(`${API_URL}/api/notificaciones/${id}/vista`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });

      setNotificaciones((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, leidaPor: [...(n.leidaPor || []), userId] } : n
        )
      );
    } catch (error) {
      console.error("❌ Error marcando como leída:", error);
    }
  };

  // Borrar notificaciones
  const borrarNotificaciones = async () => {
    try {
      await fetch(`${API_URL}/api/notificaciones/${userId}`, {
        method: "DELETE",
      });
      setNotificaciones([]);
    } catch (error) {
      console.error("❌ Error ocultando notificaciones:", error);
    }
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotificaciones(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Logout
  const confirmLogout = () => {
    setShowLogoutModal(false);
    onLogout?.();
  };

  // Config
  const handleConfigClick = () => {
    if (userData?.rol.toLowerCase() === "admin") navigate("/hub");
    else toast.info("¡Lo sentimos! No puedes acceder a este espacio");
  };

  return (
    <>
      <header className="topbar">
        {/* 🔹 YA NO EXISTE EL BUSCADOR */}

        <div className="topbar-actions">
          {/* 🔔 Notificaciones */}
          <div className="notificaciones-container" ref={notifRef}>
            <div
              className="notif-icon-wrapper"
              onClick={() => setShowNotificaciones(!showNotificaciones)}
            >
              <FiBell className="icon clickable" title="Notificaciones" />
              {notificaciones.length > 0 && (
                <span className="notif-count">{notificaciones.length}</span>
              )}
            </div>

            {showNotificaciones && (
              <div className="notif-dropdown">
                <h4>Notificaciones</h4>

                {notificaciones.length === 0 ? (
                  <p className="no-notifs">No tienes notificaciones</p>
                ) : (
                  <>
                    <ul>
                      {notificaciones.map((n) => {
                        const yaLeida = n.leidaPor?.includes(userId);
                        return (
                          <li
                            key={n._id}
                            className={yaLeida ? "leida" : ""}
                            onClick={() => marcarComoLeida(n._id)}
                          >
                            <strong>{n.title}</strong>
                            <p>{n.message}</p>
                            <small>{new Date(n.fecha).toLocaleString()}</small>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="notif-footer">
                      <button
                        className="btn-icon-borrar"
                        onClick={borrarNotificaciones}
                        title="Borrar todas las notificaciones"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ⚙️ Configuración */}
          <FiSettings
            className="icon clickable"
            title="Configuración"
            onClick={handleConfigClick}
          />

          {/* 🔒 Logout */}
          <button
            className="logout-button"
            onClick={() => setShowLogoutModal(true)}
            title="Cerrar sesión"
          >
            <FiLogOut className="icon" />
          </button>

          {/* 👤 Perfil */}
          <div className="user-info" onClick={() => setShowMenu(!showMenu)}>
            <img
              src={userData?.foto || noProfile}
              alt="Perfil"
              className="profile-pic"
            />
            <span>
              {greeting}, {userData?.nombre?.split(" ")[0] || "Usuario"}! {emoji}
            </span>

            {showMenu && (
              <ProfileMenu
                onLogout={() => setShowLogoutModal(true)}
                onClose={() => setShowMenu(false)}
              />
            )}
          </div>
        </div>
      </header>

      {/* Modal de cierre de sesión */}
      {showLogoutModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowLogoutModal(false)}
        >
          <div className="modal small" onClick={(e) => e.stopPropagation()}>
            <h3>¿Deseas Cerrar Sesión?</h3>
            <p>
              Tu sesión actual se cerrará y deberás iniciar sesión nuevamente.
            </p>
            <div className="modal-actions">
              <button
                className="top-btn-secondary"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancelar
              </button>
              <button className="top-btn-primary" onClick={confirmLogout}>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Topbar;
